// Regroupement des classements UTR et ATP en tranches, pour la liste teaser du
// freemium. Le classement FFT et l'âge y sont affichés en valeur réelle, comme
// dans la liste complète (arbitrage client du 28/07/2026).
//
// Les bornes reprennent celles des filtres de la liste joueurs de la V2, pour
// qu'une tranche affichée corresponde à une tranche filtrable. Seul le libellé
// diffère : ici « 8-9 » là où la V2 écrit « 8 à 9 ».

export const UTRRankingBrackets = ["8-or-less", "8-9", "9-10", "10-11", "11-12", "12-13", "13-or-more"] as const;
export type UTRRankingBracket = (typeof UTRRankingBrackets)[number];

// UTR est stocké en texte ("12.70"). Bornes (basse, haute] : une valeur tombe dans
// une seule tranche.
export function utrRankingBracket(ranking: string | null): UTRRankingBracket | null {
  if (!ranking) {
    return null;
  }
  const value = Number.parseFloat(ranking);
  if (!Number.isFinite(value)) {
    return null;
  }

  if (value <= 8) {
    return "8-or-less";
  }
  if (value <= 9) {
    return "8-9";
  }
  if (value <= 10) {
    return "9-10";
  }
  if (value <= 11) {
    return "10-11";
  }
  if (value <= 12) {
    return "11-12";
  }
  if (value <= 13) {
    return "12-13";
  }
  return "13-or-more";
}

// Bornes (min exclusive, max incluse) de chaque tranche UTR, pour filtrer en SQL
// sans réimplémenter le découpage ci-dessus. Toute modification des bornes doit
// être répercutée dans utrRankingBracket, sinon une tranche affichée ne
// correspondrait plus à la tranche filtrée.
export const UTRRankingBracketBounds: Record<UTRRankingBracket, { min: number | null; max: number | null }> = {
  "8-or-less": { min: null, max: 8 },
  "8-9": { min: 8, max: 9 },
  "9-10": { min: 9, max: 10 },
  "10-11": { min: 10, max: 11 },
  "11-12": { min: 11, max: 12 },
  "12-13": { min: 12, max: 13 },
  "13-or-more": { min: 13, max: null },
};

// Les tranches hautes sont ouvertes côté filtre (« +500 » = tout classement
// au-delà de 500) et se chevauchent donc entre elles ; à l'affichage il faut une
// tranche unique par joueur, donc « +500 » se lit 501-1000 et « +1000 »
// 1001-1500. Aucune borne haute sur la dernière : un classement au-delà de 2000
// tombe dans « +1500 » plutôt que null (qui s'afficherait « pas de classement »).
export const ATPRankingBrackets = ["1-250", "250-500", "over-500", "over-1000", "over-1500"] as const;
export type ATPRankingBracket = (typeof ATPRankingBrackets)[number];

export function atpRankingBracket(ranking: string | null): ATPRankingBracket | null {
  if (!ranking) {
    return null;
  }
  const value = Number.parseInt(ranking, 10);
  if (!Number.isFinite(value) || value < 1) {
    return null;
  }

  if (value <= 250) {
    return "1-250";
  }
  if (value <= 500) {
    return "250-500";
  }
  if (value <= 1000) {
    return "over-500";
  }
  if (value <= 1500) {
    return "over-1000";
  }
  return "over-1500";
}

// Bornes exploitées côté SQL. Un classement inférieur à 1 est hors tranche
// (atpRankingBracket renvoie null), d'où la borne basse à 0 sur la première
// tranche, exclusive comme les autres. Les tranches « over-* » n'ont pas de borne
// haute : filtrer sur « +500 » remonte aussi les joueurs affichés « +1000 » et
// « +1500 ».
export const ATPRankingBracketBounds: Record<ATPRankingBracket, { min: number | null; max: number | null }> = {
  "1-250": { min: 0, max: 250 },
  "250-500": { min: 250, max: 500 },
  "over-500": { min: 500, max: null },
  "over-1000": { min: 1000, max: null },
  "over-1500": { min: 1500, max: null },
};

// Le classement ITF est stocké et saisi comme l'ATP/WTA (entier de 1 à 2000) :
// il réutilise les mêmes tranches, libellés compris.
export const ITFRankingBrackets = ATPRankingBrackets;
export type ITFRankingBracket = ATPRankingBracket;
